import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { CLIENT_TYPE_LABELS } from '../config/serviceConstants';
import '../styles/ServiceClients.css';

const ServiceClientsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeNavItem, setActiveNavItem] = useState('services');

  // Subscription data keyed by clientId
  const [subscriptionMap, setSubscriptionMap] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await serviceApi.getClients();
      const clientList = res.data?.clients || [];
      setClients(clientList);

      // Fetch subscriptions for each active client
      const subMap = {};
      await Promise.all(
        clientList.filter(c => c.status === 'active').map(async (client) => {
          try {
            const subsRes = await serviceApi.getClientSubscriptions(client._id);
            subMap[client._id] = subsRes.data?.subscriptions || [];
          } catch {
            subMap[client._id] = [];
          }
        })
      );
      setSubscriptionMap(subMap);
    } catch (err) {
      console.error('Error fetching service clients:', err);
      setError('تعذر تحميل قائمة العملاء');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived metrics
  const activeClients = clients.filter(c => c.status === 'active');
  const inactiveClients = clients.filter(c => c.status === 'inactive');

  // Search filter
  const filtered = clients.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = c.userId?.name?.toLowerCase() || '';
    const email = c.userId?.email?.toLowerCase() || '';
    const code = c.userId?.clientCode?.toLowerCase() || '';
    return name.includes(q) || email.includes(q) || code.includes(q);
  });

  // Get best balance for a client
  const getClientBalance = (clientId) => {
    const subs = subscriptionMap[clientId] || [];
    const activeSub = subs.find(s => s.status === 'active');
    if (!activeSub) return null;
    return activeSub.effectiveBalance ?? (activeSub.visitsIncluded - activeSub.visitsUsed);
  };

  const getBalanceClass = (balance) => {
    if (balance === null) return '';
    if (balance <= 0) return 'svc-balance--zero';
    if (balance <= 2) return 'svc-balance--low';
    return 'svc-balance--ok';
  };

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="svc-clients">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="عملاء خدمات النظافة"
    >
      <div className="svc-clients">
        {/* Header */}
        <div className="svc-clients__header">
          <h3 className="svc-clients__title">عملاء الخدمة</h3>
          <button
            className="svc-action svc-action--primary"
            onClick={() => navigate('/services/clients/new')}
          >
            <i className="bi bi-person-plus" />
            إضافة عميل
          </button>
        </div>

        {/* Search */}
        <div className="svc-clients__search" style={{ marginBottom: 16 }}>
          <i className="bi bi-search svc-clients__search-icon" />
          <input
            type="text"
            placeholder="بحث بالاسم أو البريد أو الكود..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="touch-input"
          />
        </div>

        {/* Stats */}
        <div className="svc-clients__stats">
          <span className="svc-clients__stat">
            <i className="bi bi-people" />
            الإجمالي: {clients.length}
          </span>
          <span className="svc-clients__stat svc-clients__stat--active">
            <i className="bi bi-check-circle" />
            نشط: {activeClients.length}
          </span>
          {inactiveClients.length > 0 && (
            <span className="svc-clients__stat svc-clients__stat--inactive">
              <i className="bi bi-pause-circle" />
              غير نشط: {inactiveClients.length}
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-exclamation-triangle" /></div>
            <p className="svc-clients__empty-text">{error}</p>
          </div>
        )}

        {/* Table */}
        {!error && filtered.length === 0 ? (
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-people" /></div>
            <p className="svc-clients__empty-text">
              {search ? 'لا توجد نتائج مطابقة' : 'لا يوجد عملاء بعد'}
            </p>
            {!search && (
              <button
                className="svc-action svc-action--outline"
                onClick={() => navigate('/services/clients/new')}
              >
                <i className="bi bi-person-plus" />
                إضافة أول عميل
              </button>
            )}
          </div>
        ) : !error && (
          <div className="svc-clients__table-wrap">
            <table className="svc-clients__table">
              <thead>
                <tr>
                  <th>العميل</th>
                  <th>النوع</th>
                  <th>الحالة</th>
                  <th>الرصيد</th>
                  <th>البريد</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((client) => {
                  const balance = getClientBalance(client._id);

                  return (
                    <tr
                      key={client._id}
                      onClick={() => navigate(`/services/clients/${client._id}`)}
                    >
                      <td>
                        <div className="svc-client-name">{client.userId?.name || '—'}</div>
                        {client.userId?.clientCode && (
                          <div className="svc-client-code">{client.userId.clientCode}</div>
                        )}
                      </td>
                      <td>
                        <span className={`svc-type-badge svc-type-badge--${client.type}`}>
                          {CLIENT_TYPE_LABELS[client.type] || client.type}
                        </span>
                      </td>
                      <td>
                        <span className={`svc-status-dot svc-status-dot--${client.status}`} />
                        {client.status === 'active' ? 'نشط' : 'غير نشط'}
                      </td>
                      <td>
                        {balance !== null ? (
                          <span className={`svc-balance ${getBalanceClass(balance)}`}>
                            {balance} زيارة
                          </span>
                        ) : (
                          <span style={{ color: '#adb5bd', fontSize: '0.82rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {client.userId?.email || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DesktopLayout>
  );
};

export default ServiceClientsPage;
